import { useCallback, useEffect, useRef, useState } from 'react'

const MINUTE_MS = 60 * 1000
// How long a suppress check may run before the warning/expiry proceeds anyway (fail safe).
const SUPPRESS_CHECK_GRACE_MS = 10 * 1000

type Params = {
  /** True only for async (online) visits — the sole case this reminder applies to. */
  enabled: boolean
  /** The claim/release window from settings (AsynchronousVisitsSettings.ClaimTimeoutMinutes). */
  claimTimeoutMinutes: number
  /** Epoch ms the current claim window started (persisted, so it survives a refresh). */
  claimStartMs: number
  /** Fired once, at `claimStartMs + claimTimeoutMinutes`, if the warning went unanswered. */
  onExpire: () => void
  /**
   * Consulted at the warning and expiry moments (not on a timer). Resolves true when the claim
   * will not be released — e.g. the provider has already sent a prescription — so the warning is
   * suppressed and `onExpire` does not fire. Evaluated lazily at each deadline so it reflects
   * fresh backend state near the timeout rather than whatever was cached when the window opened.
   * A rejection is treated as "not suppressed" (fail safe: warn/expire as usual).
   */
  shouldSuppress?: () => Promise<boolean>
}

// Single-fire completion reminder for async visits. It warns the provider one minute before the
// backend releases the claim (`claimStart + claimTimeoutMinutes - 1min`); if they don't respond,
// `onExpire` fires at the release moment (`claimStart + claimTimeoutMinutes`). No repeat/re-arm —
// the backend releases the visit at the timeout regardless. Anchored to the persisted claimStart so
// a page refresh keeps the same window rather than restarting it. `shouldSuppress` lets a caller
// stand the whole thing down when the backend won't release the claim after all (e.g. a
// prescription was sent), checked fresh at each deadline.
export function useOnlineVisitCompletionReminder({
  enabled,
  claimTimeoutMinutes,
  claimStartMs,
  onExpire,
  shouldSuppress,
}: Params) {
  const [isStillThereOpen, setIsStillThereOpen] = useState(false)
  const warningRef = useRef<ReturnType<typeof setTimeout>>()
  const expiryRef = useRef<ReturnType<typeof setTimeout>>()
  const anchorRef = useRef(claimStartMs)
  const onExpireRef = useRef(onExpire)
  const shouldSuppressRef = useRef(shouldSuppress)
  // Bumped every time the timers are (re)scheduled or cleared. `shouldSuppress` is async, so a
  // check can still be in flight when the window is reset (Continue) or stopped (defer/complete).
  // Each scheduled callback captures the generation it was armed under and bails if it changed —
  // otherwise a stale resolution could reopen the modal or fire `onExpire` on a window that moved on.
  const generationRef = useRef(0)
  useEffect(() => {
    onExpireRef.current = onExpire
  }, [onExpire])
  useEffect(() => {
    shouldSuppressRef.current = shouldSuppress
  }, [shouldSuppress])

  const clearTimers = useCallback(() => {
    generationRef.current += 1
    if (warningRef.current) {
      clearTimeout(warningRef.current)
      warningRef.current = undefined
    }
    if (expiryRef.current) {
      clearTimeout(expiryRef.current)
      expiryRef.current = undefined
    }
  }, [])

  const schedule = useCallback(() => {
    clearTimers()
    const gen = generationRef.current // captured after clearTimers bumps it; shared by both timers
    const warningAt = anchorRef.current + Math.max(claimTimeoutMinutes - 1, 0) * MINUTE_MS
    const expiryAt = anchorRef.current + claimTimeoutMinutes * MINUTE_MS
    warningRef.current = setTimeout(
      () => {
        const suppressCheck = shouldSuppressRef.current
        if (!suppressCheck) {
          setIsStillThereOpen(true)
          return
        }
        // A sent prescription can't be un-sent, so if the claim is safe now, stand down both
        // timers — no warning, and no expiry a minute later.
        // A stalled check must not swallow the warning, so after a short grace period show it
        // anyway. A late "suppressed" answer still stands both timers down and closes it.
        const graceTimer = setTimeout(() => {
          if (gen === generationRef.current) setIsStillThereOpen(true)
        }, SUPPRESS_CHECK_GRACE_MS)
        void suppressCheck()
          .finally(() => clearTimeout(graceTimer))
          .then((suppress) => {
            if (gen !== generationRef.current) return // window was reset/stopped while we awaited
            if (suppress) {
              clearTimers()
              setIsStillThereOpen(false)
            } else {
              setIsStillThereOpen(true)
            }
          })
          .catch(() => {
            if (gen === generationRef.current) setIsStillThereOpen(true)
          })
      },
      Math.max(warningAt - Date.now(), 0),
    )
    expiryRef.current = setTimeout(
      () => {
        // Reaching expiry settles this window. Invalidate any still-pending warning check (it
        // shares `gen`) so a late "not suppressed" resolution can't reopen the modal after we
        // decide here; the expiry's own check then runs under this fresh generation.
        generationRef.current += 1
        const expiryGen = generationRef.current
        const fire = () => {
          setIsStillThereOpen(false)
          onExpireRef.current()
        }
        const suppressCheck = shouldSuppressRef.current
        if (!suppressCheck) {
          fire()
          return
        }
        // Re-check at the deadline too: the provider may have sent the prescription during the
        // final minute, after the warning already showed. If so, hide the modal but don't expire.
        // A stalled check must not hold expiry open forever — the backend releases the claim at the
        // deadline regardless — so after a short grace period treat it as "not suppressed".
        let settled = false
        const settle = (suppress: boolean) => {
          if (settled || expiryGen !== generationRef.current) return // reset/stopped while we awaited
          settled = true
          clearTimeout(graceTimer)
          setIsStillThereOpen(false)
          if (!suppress) onExpireRef.current()
        }
        const graceTimer = setTimeout(() => settle(false), SUPPRESS_CHECK_GRACE_MS)
        void suppressCheck()
          .then(settle)
          .catch(() => settle(false))
      },
      Math.max(expiryAt - Date.now(), 0),
    )
  }, [claimTimeoutMinutes, clearTimers])

  // Adopt a new visit's claim-start anchor. Kept separate from scheduling so a settings-driven
  // reschedule doesn't clobber a fresh anchor set by resetWindow.
  useEffect(() => {
    anchorRef.current = claimStartMs
  }, [claimStartMs])

  useEffect(() => {
    if (!enabled) {
      clearTimers()
      return undefined
    }
    schedule()
    return clearTimers
  }, [enabled, claimStartMs, schedule, clearTimers])

  // Continue success: start a fresh window anchored at `anchorMs` (the persisted new claim-start).
  const resetWindow = useCallback(
    (anchorMs: number) => {
      anchorRef.current = anchorMs
      setIsStillThereOpen(false)
      schedule()
    },
    [schedule],
  )

  // Hard stop: hide the modal and cancel timers (defer / complete / unresolved / failed re-claim).
  const stop = useCallback(() => {
    setIsStillThereOpen(false)
    clearTimers()
  }, [clearTimers])

  return { isStillThereOpen, resetWindow, stop }
}
